const dayKey = (date: Date, timeZone: string) =>
  date.toLocaleDateString('fr-FR', { timeZone, year: 'numeric', month: 'numeric', day: 'numeric' })

/** `14:05` the same day in the event's time zone, `sam. 6 févr., 14:05` otherwise. */
export function formatSentAt(sentAt: string, timeZone: string, now = new Date()): string {
  const date = new Date(sentAt)
  const time = date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone })
  if (dayKey(date, timeZone) === dayKey(now, timeZone)) return time
  const day = date.toLocaleDateString('fr-FR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone,
  })
  return `${day}, ${time}`
}

/** `YYYY-MM-DD` of the date in the time zone. */
const isoDay = (date: Date, timeZone: string) => date.toLocaleDateString('en-CA', { timeZone })

/** The ISO day `count` days before `day`. */
function daysBefore(day: string, count: number): string {
  const [year = 0, month = 1, date = 1] = day.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, date - count)).toISOString().slice(0, 10)
}

/** `14:05` in the event's time zone. */
export function formatTime(sentAt: string, timeZone: string): string {
  return new Date(sentAt).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  })
}

/** Date of the latest message in a list: `14:05` today, `Hier`, `lun.` within a week, `06/02/2027` before. */
export function formatListDate(sentAt: string, timeZone: string, now = new Date()): string {
  const date = new Date(sentAt)
  const day = isoDay(date, timeZone)
  const today = isoDay(now, timeZone)
  if (day === today) return formatTime(sentAt, timeZone)
  if (day === daysBefore(today, 1)) return 'Hier'
  if (day > daysBefore(today, 7)) {
    const weekday = date.toLocaleDateString('fr-FR', { weekday: 'short', timeZone })
    return weekday.charAt(0).toUpperCase() + weekday.slice(1)
  }
  return date.toLocaleDateString('fr-FR', { timeZone })
}

/** `Aujourd’hui`, `Hier`, `samedi 6 février`, with the year when not the current one. */
function dayLabel(sentAt: string, timeZone: string, now: Date): string {
  const date = new Date(sentAt)
  const day = isoDay(date, timeZone)
  const today = isoDay(now, timeZone)
  if (day === today) return 'Aujourd’hui'
  if (day === daysBefore(today, 1)) return 'Hier'
  const label = date.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: day.slice(0, 4) === today.slice(0, 4) ? undefined : 'numeric',
    timeZone,
  })
  return label.charAt(0).toUpperCase() + label.slice(1)
}

/** Messages in order, split by day in the event's time zone. */
export function byDay<T extends { sent_at: string }>(
  messages: T[],
  timeZone: string,
  now = new Date(),
): { day: string; label: string; messages: T[] }[] {
  const days: { day: string; label: string; messages: T[] }[] = []
  for (const message of messages) {
    const day = isoDay(new Date(message.sent_at), timeZone)
    const last = days[days.length - 1]
    if (last?.day === day) last.messages.push(message)
    else days.push({ day, label: dayLabel(message.sent_at, timeZone, now), messages: [message] })
  }
  return days
}

const normalize = (text: string) =>
  text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()

/** Whether one of the fields contains `query`, ignoring case and accents. */
export function matchesQuery(fields: (string | null)[], query: string): boolean {
  const wanted = normalize(query.trim())
  return fields.some((field) => field != null && normalize(field).includes(wanted))
}

/** `VB` for `Valentine B.`: the first letters of the first two words. */
export const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join('')
